import { z } from 'zod';
import type {
  ExtractedBrandData,
  BrandSearchCandidate,
  ExtractBrandInfoResult,
} from '../types/brand.types';

export const addBrandSchema = z.object({
  name: z.string().trim().min(1, 'Brand name is required'),
  description: z.string().optional(),
  industry: z.string().optional(),
  website_url: z.string().url('Invalid website URL').or(z.literal('')).optional(),
  logo_url: z.string().url('Invalid logo URL').or(z.literal('')).optional(),
  primary_contact_id: z.string().optional(),
});

export const updateBrandSchema = addBrandSchema.partial().extend({
  id: z.string().min(1, 'Brand ID is required'),
});

/** Postgres unique_violation — raised by the brands unique_org_name constraint. */
export const UNIQUE_VIOLATION = '23505';

export const toAddBrandErrorMessage = (error: unknown): string => {
  if (typeof error === 'object' && error !== null && 'code' in error) {
    const { code } = error as { code?: unknown };
    if (code === UNIQUE_VIOLATION) {
      return 'A brand with this name already exists in your organization. Try a different name.';
    }
  }

  if (error instanceof Error && error.message) return error.message;
  return 'There was an error adding the brand. Please try again.';
};

// Type Guards for Extracted Brand Data
const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const isNullableString = (value: unknown): value is string | null =>
  value === null || typeof value === 'string';

export const isExtractedBrandData = (value: unknown): value is ExtractedBrandData =>
  isRecord(value) &&
  typeof value.domain === 'string' &&
  typeof value.brandName === 'string' &&
  typeof value.websiteUrl === 'string' &&
  isNullableString(value.description) &&
  isNullableString(value.logoUrl);

export const isBrandSearchCandidate = (value: unknown): value is BrandSearchCandidate =>
  isRecord(value) &&
  typeof value.name === 'string' &&
  typeof value.domain === 'string' &&
  isNullableString(value.logoUrl);

export const isExtractBrandInfoResult = (value: unknown): value is ExtractBrandInfoResult => {
  if (!isRecord(value)) return false;

  if (value.status === 'candidates') {
    return (
      typeof value.query === 'string' &&
      Array.isArray(value.results) &&
      value.results.every(isBrandSearchCandidate)
    );
  }

  if (value.status === 'duplicate') {
    return (
      typeof value.domain === 'string' &&
      typeof value.brandId === 'string' &&
      typeof value.brandName === 'string'
    );
  }

  if (value.status === 'ok') {
    return (
      (value.source === 'cache' || value.source === 'extracted') &&
      isExtractedBrandData(value.data)
    );
  }

  return false;
};
